import React, { useState, useEffect } from 'react';
import { getUsersByLocalId } from '../../../../../../crud/users_api/users';
import Table from './components/Table/Table';
import MiniLoadingSpinner from '../../../../../../components/Spinners/MiniLoadingSpinner';
import { PuestoOption, RoleOption } from './components/Table/types';
import { User } from '../../../types';
import ActiveFilter, { ActiveFilterItem } from '../../../../../../components/common/ActiveFilter/ActiveFilter';
import { getPuestosByLocalId } from '../../../../../../crud/management_api/puestos';
import { getAllRoles } from '../../../../../../crud/configuration_api/roles';
import FilterBar from './components/FilterBar/FilterBar';

interface FilterOption {
  value: string | number;
  label: string;
}

const UsuariosView: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [puestos, setPuestos] = useState<PuestoOption[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [selectedPuesto, setSelectedPuesto] = useState<FilterOption[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<FilterOption[]>([]);
  const [selectedEstado, setSelectedEstado] = useState<FilterOption[]>([]);
  
  const [searchValue, setSearchValue] = useState('');

  // Selection state
  const [showSelection, setShowSelection] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchUsers();
  }, []);

  // Obtener puestos y roles al montar el componente
  useEffect(() => {
    const fetchPuestosAndRoles = async () => {
      try {
        const userContext = localStorage.getItem('user');
        const localId = userContext ? JSON.parse(userContext).local_id : 0;
        const puestosResponse = await getPuestosByLocalId(localId);
        setPuestos(puestosResponse.data);
        const rolesResponse = await getAllRoles();
        setRoles(rolesResponse.data);
      } catch (error) {
        console.error('Error obteniendo puestos y roles:', error);
      }
    };
    fetchPuestosAndRoles();
  }, []);


  const fetchUsers = async () => {
    try {
      setLoading(true);
      const userContext = localStorage.getItem('user');
      const localId = userContext ? JSON.parse(userContext).local_id : 0;
      const response = await getUsersByLocalId(localId);
      
      // Sort users alphabetically by username
      const sortedUsers = response.data.sort((a: User, b: User) => {
        const nameA = `${a.username} ${a.last_name}`.toLowerCase();
        const nameB = `${b.username} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setUsers(sortedUsers);
      setFilteredUsers(sortedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter handlers
  const handlePuestoChange = (selected: FilterOption[]) => {
    setSelectedPuesto(selected);
  };

  const handleRolesChange = (selected: FilterOption[]) => {
    setSelectedRoles(selected);
  };

  const handleEstadoChange = (selected: FilterOption[]) => {
    setSelectedEstado(selected);
  };

  // Apply filters to users
  useEffect(() => {
    let filtered = users;

    // Apply Puesto filter
    if (selectedPuesto.length > 0) {
      const selectedPuestoIds = selectedPuesto.map(p => p.value);
      filtered = filtered.filter(user => selectedPuestoIds.includes(user.puesto_id));
    }

    // Apply Roles filter
    if (selectedRoles.length > 0) {
      const selectedRoleIds = selectedRoles.map(r => r.value);
      filtered = filtered.filter(user => selectedRoleIds.includes(user.rol_id));
    }

    // Apply Estado filter
    if (selectedEstado.length > 0) {
      const selectedEstadoValues = selectedEstado.map(e => Boolean(e.value));
      filtered = filtered.filter(user => selectedEstadoValues.includes(user.state));
    }

    // Apply search filter
    if (searchValue.trim()) {
      const q = searchValue.toLowerCase();
      filtered = filtered.filter(u =>
        `${u.username} ${u.last_name}`.toLowerCase().includes(q)
      );
    }

    setFilteredUsers(filtered);
  }, [users, selectedPuesto, selectedRoles, selectedEstado, searchValue]);

  // Selection handlers
  const handleToggleSelection = () => {
    setShowSelection(!showSelection);
    if (showSelection) {
      setSelectedUsers(new Set());
    }
  };

  const handleCreateUser = () => {
    // This will trigger the create user modal in the Table component
    // We need to pass this down to the Table component
    setShowCreateModal(true);
  };

  // Add state for create modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleDeleteSelection = async () => {
    if (selectedUsers.size === 0) {
      return;
    }
    
    try {
      const { deactivateSelectedUsers } = await import('../../../../../../crud/users_api/users');
      const { toast } = await import('react-toastify');
      
      const selectedUserIds = Array.from(selectedUsers).map(id => String(id));
      await deactivateSelectedUsers(selectedUserIds);
      toast.success('Usuarios desactivados correctamente');
      
      // Update the users list to reflect the changes
      const updatedUsers = users.map(u =>
        selectedUsers.has(parseInt(u.user_id)) ? { ...u, state: false } : u
      );
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      
      // Clear selection
      setSelectedUsers(new Set());
    } catch (error) {
      const { toast } = await import('react-toastify');
      console.error('Error deactivating users:', error);
      toast.error('Error al desactivar usuarios');
    }
  };

  // Build active filters
  const activeFilters: ActiveFilterItem[] = [];
  if (selectedPuesto.length > 0) {
    selectedPuesto.forEach((p: FilterOption) => {
      activeFilters.push({ label: `Puesto: ${p.label}`, value: `puesto_${p.value}` });
    });
  }
  if (selectedRoles.length > 0) {
    selectedRoles.forEach((r: FilterOption) => {
      activeFilters.push({ label: `Rol: ${r.label}`, value: `rol_${r.value}` });
    });
  }
  if (selectedEstado.length > 0) {
    selectedEstado.forEach((e: FilterOption) => {
      activeFilters.push({ label: `Estado: ${e.label}`, value: `estado_${e.value}` });
    });
  }

  // Function to remove filters
  const handleRemoveFilter = (value: string) => {
    if (value.startsWith('puesto_')) {
      const id = value.replace('puesto_', '');
      setSelectedPuesto(selectedPuesto.filter((p: FilterOption) => String(p.value) !== id));
    } else if (value.startsWith('rol_')) {
      const id = value.replace('rol_', '');
      setSelectedRoles(selectedRoles.filter((r: FilterOption) => String(r.value) !== id));
    } else if (value.startsWith('estado_')) {
      const id = value.replace('estado_', '');
      setSelectedEstado(selectedEstado.filter((e: FilterOption) => String(e.value) !== id));
    }
  };

  if (loading) return <MiniLoadingSpinner />;

  return (
    <>
      {/* FilterBar with integrated search */}
      <FilterBar
        puestosOptions={puestos}
        rolesOptions={roles}
        selectedPuesto={selectedPuesto}
        selectedRoles={selectedRoles}
        selectedEstado={selectedEstado}
        onPuestoChange={handlePuestoChange}
        onRolesChange={handleRolesChange}
        onEstadoChange={handleEstadoChange}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        showSelection={showSelection}
        selectedUsersCount={selectedUsers.size}
        onCreateUser={handleCreateUser}
        onToggleSelection={handleToggleSelection}
        onDeleteSelection={handleDeleteSelection}
      />

      {/* Active Filters */}
      <ActiveFilter filters={activeFilters} onRemove={handleRemoveFilter} />

      {/* Users Table */}
      <Table 
        data={filteredUsers} 
        setData={setUsers} 
        puestos={puestos} 
        roles={roles} 
        showSelection={showSelection}
        selectedUsers={selectedUsers}
        onUserSelectionChange={setSelectedUsers}
        showCreateModal={showCreateModal}
        onCloseCreateModal={() => setShowCreateModal(false)}
      />
    </>
  );
};

export default UsuariosView;
